import type { JobLog } from "@/api/raptor/job-log";
import { useApi, useGetAll } from "@mb-pro-ui/utils";
import { Send as SendIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "wouter";

const JobLogChat = () => {
  const { formatMessage } = useIntl();
  const api = useApi();
  const { jobId } = useParams<{ jobId: string }>();

  const { data, isLoading, isError, refetch } = useGetAll<JobLog>(
    "raptor/job-log",
    {
      filter: { job: { eq: jobId } },
    },
  );

  const [message, setMessage] = useState("");

  const createLogMutation = useMutation({
    mutationFn: async (text: string) => {
      return api(`/actions/raptor/jobs/${jobId}/log`, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
        },
        body: text,
      });
    },
    onSuccess: () => {
      setMessage("");
      refetch();
    },
    onError: (error: Error) => {
      console.error("Hiba a napló létrehozásakor:", error);
    },
  });

  const handleSend = () => {
    if (!message.trim()) return;
    createLogMutation.mutate(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getUsername = (fullUser: string) => {
    const parts = fullUser.split("/");
    return parts[parts.length - 1];
  };

  const getUserInitials = (fullUser: string) => {
    const username = getUsername(fullUser);
    return username
      .split(".")
      .map((name) => name.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ backgroundColor: "#f5f5f5" }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        p={3}
        textAlign="center"
        sx={{ backgroundColor: "#f5f5f5", minHeight: "200px" }}
      >
        <Typography color="error" variant="h6">
          {formatMessage({
            defaultMessage: "An error occurred while loading the log.",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#f5f5f5",
        borderRadius: 2,
        overflow: "hidden",
        mt: 3,
      }}
    >
      <Box
        flex={1}
        sx={{
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxHeight: "400px",
          "-webkit-overflow-scrolling": "touch",
        }}
      >
        {data?.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              {formatMessage({
                defaultMessage:
                  "No records have been logged for this operation yet.",
              })}
            </Typography>
          </Box>
        ) : (
          data?.map((log) => (
            <Box key={log.id} display="flex" gap={1.5} alignItems="flex-start">
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: (theme) => theme.palette.primary.main,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {getUserInitials(log.user)}
              </Avatar>

              <Box flex={1}>
                <Box mb={1} display="flex" gap={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  >
                    {getUsername(log.user)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(log.ts).toLocaleString("hu-HU", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "white",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    maxWidth: "85%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.4,
                      wordBreak: "break-word",
                    }}
                  >
                    {log.text}
                  </Typography>
                </Card>
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          backgroundColor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={formatMessage({ defaultMessage: "Comment" })}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#f8f9fa",
              "& .MuiOutlinedInput-input": {
                fontSize: "0.85rem",
              },
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
                borderWidth: 2,
              },
            },
          }}
        />

        <IconButton
          onClick={handleSend}
          disabled={createLogMutation.isPending || !message.trim()}
          sx={{
            color: "primary.main",
            width: 44,
            height: 44,
            "&:hover": {
              backgroundColor: "primary.dark",
            },
            "&.Mui-disabled": {
              color: "action.disabled",
            },
          }}
        >
          {createLogMutation.isPending ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default JobLogChat;
